"use client";

import React from "react";
import { cn } from "@/lib/utils";

const COLORS = {
  trunk:       "#7A5230",
  trunkDead:   "#6B5642",
  canopyDark:  "#3F8E4F",
  canopyMid:   "#4CAF50",
  canopyLight: "#6CC469",
  leafDead:    "#5B7A3F", // Used for the green wilted stem/leaves
  leafFalling: "#7A9450",
  petalDark:   "#A6332C",
  petalMid:    "#C24A3F",
  petalLight:  "#D9695C",
  petalCenter: "#6B1C17",
  messageGood: "#3F8E4F",
  messageBad:  "#C0392B",
};

export function ImpactPlant({ className = "", trend }) {
  const isDead = trend === "up";

  const message = isDead
    ? "Contaminations are increasing..."
    : "Good job reducing contaminations!";
  const messageColor = isDead ? COLORS.messageBad : COLORS.messageGood;

  return (
    <div className={cn("flex items-center gap-2 select-none", className)}>
      <svg width="30" height="34" viewBox="0 0 30 34" style={{ flexShrink: 0, overflow: "visible" }}>
        {isDead ? (
          <g className="impact-plant-dead-stem">
            {/* Green wilted stem curving right and drooping */}
            <path
              d="M13 32 C13 22, 10 12, 16 11 C21 10, 23 15, 21 18"
              stroke={COLORS.leafDead}
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />

            {/* Left leaf (drooping down) */}
            <g className="impact-plant-dead-leaf-l">
              <path 
                d="M13.5 24 C10 22, 6 25, 8 28 C9 29, 12 26, 13.5 24 Z" 
                fill={COLORS.leafDead} 
              />
            </g>

            {/* Right leaf (pointing up near the bend) */}
            <g className="impact-plant-dead-leaf-r">
              <path 
                d="M15 15 C18 13, 21 14, 21 17 C20 18, 17 17, 15 15 Z" 
                fill={COLORS.leafDead} 
              />
            </g>

            {/* Rose flower head hanging downwards */}
            <g className="impact-plant-dead-head">
              {/* Green receptacle holding the flower */}
              <path d="M19 17 C20 18.5, 22 18.5, 23 17 L21 19 Z" fill={COLORS.leafDead} />
              
              {/* Layered rose petals */}
              <path d="M17 18 C16 15, 26 15, 25 18 C26 22, 23 26, 21 27 C19 26, 16 22, 17 18 Z" fill={COLORS.petalMid} />
              <path d="M18 19 C17 17, 24 17, 24 19 C24 22, 22 25, 21 26 C20 25, 18 22, 18 19 Z" fill={COLORS.petalDark} />
              <path d="M19.5 20 C18.5 19, 22.5 19, 22.5 20 C22.5 21.5, 21.5 23.5, 21 24 C20.5 23.5, 19.5 21.5, 19.5 20 Z" fill={COLORS.petalLight} />
            </g>

            {/* Falling red petals */}
            <g className="impact-plant-falling-petal-1">
              <path d="M24 23 C23 23, 23 25, 24 26 C25 25, 25 23, 24 23 Z" fill={COLORS.petalMid} />
            </g>
            <g className="impact-plant-falling-petal-2">
              <path d="M21 28 C20 28, 20 30, 21 31 C22 30, 22 28, 21 28 Z" fill={COLORS.petalLight} />
            </g>
          </g>
        ) : (
          <g className="impact-plant-stem">
            <path d="M15 30 L15 15" stroke={COLORS.trunk} strokeWidth="2.6" strokeLinecap="round" />

            <g className="impact-plant-leaf impact-plant-leaf-l">
              <circle cx="8.5" cy="12.5" r="7.5" fill={COLORS.canopyMid} />
            </g>
            <g className="impact-plant-leaf impact-plant-leaf-r">
              <circle cx="21.5" cy="12.5" r="7.5" fill={COLORS.canopyLight} />
            </g>
            <g className="impact-plant-leaf impact-plant-leaf-top">
              <circle cx="15" cy="6.5" r="8" fill={COLORS.canopyDark} />
            </g>
          </g>
        )}
      </svg>

      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: messageColor,
          whiteSpace: "nowrap",
        }}
        className="hidden lg:inline"
      >
        {message}
      </span>

      <style>{`
        /* Healthy Plant Animations */
        .impact-plant-stem {
          transform-origin: 15px 30px;
          animation: impact-plant-wind-main 4.5s ease-in-out infinite;
        }
        .impact-plant-leaf-l {
          transform-origin: 8.5px 12.5px;
          animation: impact-plant-wind-leaf 4.5s ease-in-out infinite;
        }
        .impact-plant-leaf-r {
          transform-origin: 21.5px 12.5px;
          animation: impact-plant-wind-leaf 4.5s ease-in-out infinite 0.12s;
        }
        .impact-plant-leaf-top {
          transform-origin: 15px 6.5px;
          animation: impact-plant-wind-leaf 4.5s ease-in-out infinite 0.22s;
        }

        @keyframes impact-plant-wind-main {
          0%   { transform: rotate(0deg); }
          15%  { transform: rotate(4deg); }
          30%  { transform: rotate(-1deg); }
          50%  { transform: rotate(2deg); }
          65%  { transform: rotate(-3deg); }
          85%  { transform: rotate(1deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes impact-plant-wind-leaf {
          0%   { transform: rotate(0deg); }
          15%  { transform: rotate(6deg); }
          30%  { transform: rotate(-2deg); }
          50%  { transform: rotate(4deg); }
          65%  { transform: rotate(-5deg); }
          85%  { transform: rotate(2deg); }
          100% { transform: rotate(0deg); }
        }

        /* Dead Rose Animations */
        .impact-plant-dead-stem {
          transform-origin: 13px 32px;
          animation: impact-plant-droop-main 5.5s ease-in-out infinite;
        }
        .impact-plant-dead-leaf-l {
          transform-origin: 13.5px 24px;
          animation: impact-plant-droop-leaf 5.5s ease-in-out infinite;
        }
        .impact-plant-dead-leaf-r {
          transform-origin: 15px 15px;
          animation: impact-plant-droop-leaf 5.5s ease-in-out infinite 0.2s;
        }
        .impact-plant-dead-head {
          transform-origin: 21px 18px;
          animation: impact-plant-droop-head 5.5s ease-in-out infinite 0.08s;
        }

        @keyframes impact-plant-droop-main {
          0%   { transform: rotate(0deg); }
          20%  { transform: rotate(2deg); }
          45%  { transform: rotate(-1.5deg); }
          70%  { transform: rotate(1.5deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes impact-plant-droop-leaf {
          0%   { transform: rotate(0deg); }
          20%  { transform: rotate(3deg); }
          45%  { transform: rotate(-2deg); }
          70%  { transform: rotate(2deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes impact-plant-droop-head {
          0%   { transform: rotate(0deg); }
          20%  { transform: rotate(4deg); }
          45%  { transform: rotate(-3deg); }
          70%  { transform: rotate(3deg); }
          100% { transform: rotate(0deg); }
        }

        /* Falling Petals */
        .impact-plant-falling-petal-1 {
          transform-origin: 24px 23px;
          animation: impact-plant-fall 3.2s ease-in infinite;
        }
        .impact-plant-falling-petal-2 {
          transform-origin: 21px 28px;
          animation: impact-plant-fall 3.8s ease-in infinite 1.1s;
        }

        @keyframes impact-plant-fall {
          0%   { transform: translateY(-4px) rotate(0deg); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(12px) rotate(45deg); opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .impact-plant-stem,
          .impact-plant-leaf-l,
          .impact-plant-leaf-r,
          .impact-plant-leaf-top,
          .impact-plant-dead-stem,
          .impact-plant-dead-leaf-l,
          .impact-plant-dead-leaf-r,
          .impact-plant-dead-head,
          .impact-plant-falling-petal-1,
          .impact-plant-falling-petal-2 {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ImpactPlant;